import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import AvatarDefault from "../../../assets/images/avatar/avatar-empty.svg";
import IconAvatar from "../../../assets/images/avatar/avatar_builder_body_selected.svg.svg";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LockIcon from "@mui/icons-material/Lock";
import { BUCKET_SHOP_IMAGES } from "../../../constants/api";
import * as kRoutes from "../../../constants/routes";

const AvatarOptions = ({
  onSelect,
  avatarList,
  chosenAvatar,
  modalRef,
  onClose,
  onCloseMenu,
}) => {
  const { t } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  //Avatar default, quando ainda não tem avatar comprado
  const defaultAvatar = { id: "default", img: AvatarDefault, locked: false };

  // Estado que guarda qual avatar está selecionado pelo usuário (índice da lista)
  const [selectedAvatar, setSelectedAvatar] = useState(defaultAvatar);

  const navigate = useNavigate();

  // Cria uma lista ordenada de avatares (desbloqueados antes dos bloqueados)
  const sortedAvatarList = useMemo(() => {
    if (!avatarList) return [];
    return avatarList
      .map((avatar) => ({
        slug: avatar.slug,
        img: BUCKET_SHOP_IMAGES(avatar.slug),
        locked: avatar.owned === false,
      }))
      .sort((a, b) => a.locked - b.locked);
  }, [avatarList]);

  //função para mostrar qual avatar será exibido como selecionado, se nenhum mostra o default por padrão
  const selectedImage =
    chosenAvatar !== null
      ? sortedAvatarList?.find((item) => item?.slug === chosenAvatar?.slug)
      : defaultAvatar;

  // Sincroniza o avatar selecionado com o avatar escolhido externamente (chosenAvatar)
  useEffect(() => {
    if (selectedImage && selectedAvatar?.slug !== selectedImage?.slug) {
      setSelectedAvatar(selectedImage);
    }
  }, [selectedImage, selectedAvatar]);

  //Navega para a lojinha e fecha o modal de avatar
  const handleNavigateToStore = () => {
    // Fecha o modal de seleção de avatar
    if (onClose) onClose();

    // Fecha o menu lateral caso esteja aberto
    onCloseMenu?.();

    // Avança o modal interno (se houver) — opcional
    modalRef?.current?.nextModal(0);

    // Navega para a lojinha
    navigate(kRoutes.R_COINSTORE);
  };

  return (
    <Box
      sx={{
        maxWidth: "880px",
        width: "100%",
        height: isMobile ? "" : "484px",
        backgroundColor: "#FFFFFF",
        borderRadius: "10px",
        border: "solid 1px #aaacacff",
        display: "flex",
        padding: "16px",
        boxSizing: "border-box",
        flexDirection: isMobile ? "column" : "row",
      }}
    >
      {/* --- COLUNA ESQUERDA: Visualização grande do avatar selecionado --- */}
      <Box
        sx={{
          flex: 1,
          height: "100%",
          paddingRight: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <img
          src={selectedAvatar.img} //Imagem do avatar atual
          alt="Avatar do usuário selecionado"
          style={{
            maxWidth: isMobile ? "70%" : "100%",
            maxHeight: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      </Box>

      {/* --- COLUNA DIREITA: lista + opções --- */}
      <Box
        sx={{
          flex: 1,
          height: "100%",
          paddingLeft: isMobile ? "0" : "16px",
          borderLeft: isMobile ? "none" : "1px solid #D9D9D9",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* TÍTULO */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            marginTop: "10px",
            paddingBottom: "12px",
            marginBottom: isMobile ? "0" : "16px",
            justifyContent: isMobile ? "center" : "",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            {t("global.choose_an_avatar")}
          </Typography>
          <img src={IconAvatar} alt="icone" style={{ width: 20, height: 20 }} />
        </Box>

        {/* GRID COM SCROLL */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 2,
            padding: 2,
            border: "1px solid #ddd",
            borderRadius: "8px",
            overflowY: "auto",
            maxHeight: isMobile ? "210px" : "320px",
          }}
        >
          {sortedAvatarList?.map((avatar, i) => (
            <Box
              key={i}
              sx={{
                position: "relative",
                height: 80,
                backgroundColor: avatar.locked
                  ? "#d0d0d0"
                  : selectedAvatar.slug === avatar.slug
                    ? "rgba(11, 227, 7, 0.11)"
                    : "#f3f3f3",
                borderRadius: "6px",
                border: avatar.locked
                  ? "2px solid #bfbfbf"
                  : selectedAvatar.slug === avatar.slug
                    ? "2px solid #0be30794"
                    : "2px solid #f3f3f3",
                cursor: avatar.locked ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "0.2s",
              }}
              onClick={() => {
                if (avatar.locked) return; // impede clique no avatar bloqueado
                setSelectedAvatar(avatar);
                onSelect(avatar);
              }}
            >
              <img
                src={avatar.img}
                alt={avatar.slug}
                style={{
                  width: 75,
                  height: 75,
                  opacity: avatar.locked ? 0.4 : 1,
                }}
              />
              {/* Estilo do  avatar bloqueado */}
              {avatar.locked && (
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    top: 0,
                    left: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: "45px",
                      height: "45px",
                      borderRadius: "50%",
                      backgroundColor: "#FFFFFF",
                      alignItems: "center",
                      justifyContent: "center",
                      display: "flex",
                      boxShadow: "0 0 6px rgba(0,0,0,0.15)",
                    }}
                  >
                    <LockIcon sx={{ fontSize: 32, color: "#555" }} />
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {/* RODAPÉ COM INFO DA LOJINHA */}
        <Box
          sx={{
            display: "flex",
            mt: "auto",
            pt: 2,
            gap: "10px",
            fontSize: "14px",
            color: "#4b4b4b",
            fontWeight: 500,
            cursor: "pointer",
          }}
          onClick={handleNavigateToStore}
        >
          <LockOpenIcon sx={{ fontSize: 18, color: "#4b4b4b" }} />
          <Typography sx={{ fontSize: 14, color: "#4b4b4b", fontWeight: 500 }}>
            {t("global.unlock_all_avatars_in")}
            <Typography
              component="span"
              sx={{
                fontSize: 14,
                color: "#417FF4",
                fontWeight: 500,
                textDecoration: "underline",
              }}
            >
              {t("global.collection_items")}
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AvatarOptions;
